import { useState, useEffect } from 'react';
import { isPwaMode, isIOSDevice } from '../utils/pwaUtils';
import { logger } from "@/utils/logger";

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export const usePWA = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isPWA, setIsPWA] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        setIsPWA(isPwaMode());
        setIsIOS(isIOSDevice());

        const handleBeforeInstallPrompt = (e: Event) => {
            // Prevent Chrome 67+ from automatically showing the prompt
            e.preventDefault();
            // Stash the event so it can be triggered later
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setIsInstallable(true);
            logger.info('PWA install prompt captured');
        };

        const handleAppInstalled = () => {
            setIsInstallable(false);
            setDeferredPrompt(null);
            logger.info('PWA was installed');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const installPWA = async () => {
        if (!deferredPrompt) {
            logger.info('No deferred prompt available');
            return;
        }

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const choiceResult = await deferredPrompt.userChoice;

        if (choiceResult.outcome === 'accepted') {
            logger.info('User accepted the install prompt');
        } else {
            logger.info('User dismissed the install prompt');
        }

        // Clear the saved prompt since it can't be used again
        setDeferredPrompt(null);
        setIsInstallable(false);
    };

    return {
        isInstallable,
        isPWA,
        isIOS,
        installPWA
    };
};
